import { atom, type PrimitiveAtom } from "jotai";
import { SubmitAccessibility, type VerificationMethod } from "common/apis";
import { atomWithLocalStorage } from "common/storage";
import type { LoginLockout } from "@/features/auth/loginLockout";

export const emailCheckWelcomeNameAtom = atom("");
export const emailAtom = atom("");
export const emailValidAtom = atom(false);
export const passwordAtom = atom("");
export const SUBMIT_ACCESSIBILITY_DEFAULT: SubmitAccessibility = {
  userId: 0,
  ability: "",
  how_do_you_hear_about_us_ids: "",
  how_do_you_hear_about_us_group_ids: "",
  how_do_you_hear_option_other_text: "",
  accessibility_need_selection: "",
  makeDisabilityVisibleValue: false,
  accessibilityFeatureRequestTagsValue: "",
};
export const submitAccessibilityAtom = atom<SubmitAccessibility>(
  SUBMIT_ACCESSIBILITY_DEFAULT,
);
export const registeredUserIdAtom = atom(0);
export const drawerMenuOpenAtom = atom(false);
export const savedFiltersAppliedAtom = atom(false);
export const resetForgotPasswordCodeAtom = atom("");
// Why the reset screen is showing: a user-initiated "forgot password", or the
// server refusing a login until the password meets the current policy.
export const resetPasswordReasonAtom = atom<"forgot" | "policy">("forgot");

/**
 * A lockout the email step already learned about, handed to the password
 * screen so the user is not asked for a password they cannot use. It outlives
 * the password screen so a trip to forgot-password and back still shows it.
 *
 * The cast is load-bearing: with `strict: false`, `null` is assignable to a
 * function type, so `atom(null)` picks jotai's read-only overload and every
 * setter types as `never`. Drop it once strictNullChecks is on.
 */
export const loginLockoutAtom = atom<LoginLockout | null>(
  null,
) as PrimitiveAtom<LoginLockout | null>;
export const messageForLoginModalAtom = atom("");
/** DEV-602: how the pending account proves its email — seeded from the register response or the login error. */
export const verificationMethodAtom = atom<VerificationMethod>("link");
/** Which Figma variant the verify-email modal renders: sign-up (no header) or login (header with close). */
export const verifyEmailVariantAtom = atom<"signup" | "login">("signup");
/**
 * Epoch ms when *Resend* becomes allowed again (0 = now). Absolute, because the verify
 * screen mounts well after sign-up seeded it — the onboarding steps sit in between.
 */
export const resendAvailableUntilAtom = atom(0);

/** Turn the backend's relative `resend_available_in` seconds into the absolute atom value. */
export const resendAvailableUntil = (seconds: number | undefined | null): number =>
  Date.now() + Math.max(0, Number(seconds ?? 0)) * 1000;

/** Whole seconds left until `until`; never negative. */
export const secondsUntil = (until: number): number =>
  Math.max(0, Math.ceil((until - Date.now()) / 1000));
export const actionToPerformAfterLoginAtom = atom("");
export const actionToPerformValueAfterLoginAtom = atom("");
// Identifies which mounted surface initiated the pending post-login action, so
// that exactly one watcher resumes it (prevents stacked modals when several
// components watch the same action string). See AccessibilitySection / photo
// flag surfaces for usage.
export const actionToPerformOwnerAtom = atom("");
export const onboardingCompletedAtom = atomWithLocalStorage(
  "onboardingCompletedAtom",
  false,
);
// Own key, not the onboarding flag above — the hamburger menu resets that to
// replay the carousel, which would bring this hint back with it.
export const allPlacesHintDismissedAtom = atomWithLocalStorage(
  "allPlacesHintDismissed",
  false,
);
export const onboardingScreenAtom = atom(false);
export const onboardingCurrentScreenAtom = atom(0);
// Whether the "Ask AI" assistant is docked over the venue map column (desktop/tablet).
// Shared so the trigger (inside VenueSheetContent) and the dock (rendered in each
// page's map sidebar) stay in sync without prop-drilling through every page.
export const venueAssistantDockOpenAtom = atom(false);
